import * as _ from 'underscore';
import * as ProgramService from '../../../api/service/programService';
import * as TagService from '../../../api/service/tagService';
import * as FavoriteService from '../../../api/service/favoriteService';
import * as WorkoutService from '../../../api/service/workoutService';
import * as WorkoutElementService from '../../../api/service/workoutElementService';
import * as ExerciseService from '../../../api/service/exerciseService';
import SegmentService, {
    EVENTS as SEGMENT_EVENTS
} from '../../../../src/lib/segment/SegmentService';

import { getStore, getState } from '../../store';
import ACTIONS from './actions';
import * as CalendarState from '../calendar/calendar';
import * as ToastState from '../clipboard/toast';
import * as Utils from '../../../lib/designerUtils';
import { workoutParamTypes, repParamTypes } from '../../../lib/paramTypes';
import * as ProgramTypes from '../calendar/programTypes';
import * as DateUtils from '../../../lib/dateUtils';
import * as BlockLinker from './blockLinking/BlockLinker';

export const addNotes = (dispatch, session) => {
    const updatedSession = {
        ...session,
        has_notes: 1
    };
    dispatch({
        type: ACTIONS.SESSION_UPDATE,
        session: updatedSession
    });
    ToastState.showSuccessMessage('Session Updated');
};

export const copyAndReplace = async (day, session) => {
    ToastState.showInfoMessage('Copying Session...');
    session = session.data ? session.data : session;

    await ProgramService.overrideAndCopy(
        session.program_id,
        CalendarState.getProgramId(),
        session.id,
        'program'
    ).then(response => {
        getStore().dispatch({
            type: ACTIONS.SESSION_DELETE,
            day,
            session
        });
        CalendarState.addProgramWorkoutsToCalendar([response]);
        ToastState.showSuccessMessage('Session Copied!');
    });
};

export const createSession = async _day => {
    if (_day.date !== undefined) {
        const date = DateUtils.convertDate(_day.date);
        const month = date.format('M');
        const day = date.format('D');
        const year = date.format('YYYY');
        const session = _day.items.length;

        const response = await ProgramService.createWorkoutForDateDay(
            CalendarState.getProgramId(),
            year,
            month,
            day,
            session
        );
        getStore().dispatch({
            type: ACTIONS.SESSION_ADD,
            day: _day,
            session: response
        });
        ToastState.showSuccessMessage('Session added');

        let state = getState();
        SegmentService.track(SEGMENT_EVENTS.SessionDraftCreated.key, {
            reduxAction: ACTIONS.SESSION_ADD,
            programWorkoutId: response.id,
            dateOfTargetSession: response.date,
            calendarType: ProgramTypes.convertToString(
                CalendarState.getProgramType()
            )
        });

        return response;
    }

    const timelineDay = _day.timelineDay;
    const session = _day.items.length;
    const response = await ProgramService.createWorkoutForDay(
        CalendarState.getProgramId(),
        timelineDay,
        null,
        null,
        session
    );
    const indexes = Utils.getDayIndexesFromTimelineDay(response.timeline_day);
    const newSession = {
        ...response,
        weekIndex: indexes.weekIndex,
        index: indexes.index
    };
    getStore().dispatch({
        type: ACTIONS.SESSION_ADD,
        day: _day,
        session: newSession
    });
    ToastState.showSuccessMessage('Session added');

    let state = getState();
    SegmentService.track(SEGMENT_EVENTS.SessionDraftCreated.key, {
        reduxAction: ACTIONS.SESSION_ADD,
        programWorkoutId: newSession.id,
        dateOfTargetSession: newSession.date,
        calendarType: ProgramTypes.convertToString(
            CalendarState.getProgramType()
        )
    });
    return response;
};

export const toggleSaveSessionToLibrary = (open = true, session) => {
    getStore().dispatch({
        type: ACTIONS.UI_SAVE_SESSION_TO_LIBRARY_DIALOG,
        saveSessionToLibraryDialog: open,
        session
    });
};

export const saveSessionToLibrary = async session => {
    const newWorkout = await WorkoutService.saveWorkoutAsTemplate20(
        session.workout_id,
        session
    );
    getStore().dispatch({
        type: ACTIONS.LIBRARY_SESSION_ADD,
        session: newWorkout
    });
    toggleSaveSessionToLibrary(false, newWorkout);
    ToastState.showSuccessMessage('Session Saved in Library');
};

export const updateSession = (dispatch, session, key, value, dontSave) => {
    const updatedSession = {
        ...session
    };

    updatedSession[key] = value;
    dispatch({
        type: ACTIONS.SESSION_UPDATE,
        session: updatedSession
    });

    if (!updatedSession.instruction) {
        updatedSession.instruction = '';
    }
    if (!dontSave) {
        WorkoutService.saveWorkout(updatedSession).then(() => {
            ToastState.showSuccessMessage('Session Saved');
        });
    }
};

export const createSessionWithBlock = async (dispatch, day, blockType) => {
    const newSession = await createSession(day);
    createBlock(dispatch, newSession, blockType);
};

export const createSessionWithNotes = async (dispatch, day) => {
    const newSession = await createSession(day);
    addNotes(dispatch, newSession);
};

export const deleteBlock = (session, block) => {
    ProgramService.removeProgramWorkoutSet(block.id);
    getStore().dispatch({
        type: ACTIONS.BLOCK_DELETE,
        session: {
            data: {
                ...session
            }
        },
        block
    });

    getStore().dispatch({
        type: ACTIONS.UI_DELETE_DIALOG,
        isOpen: false
    });
    ToastState.showSuccessMessage('Block Deleted');
};

export const deleteExercise = async (block, exercise) => {
    getStore().dispatch({
        type: ACTIONS.SET_CURRENT_BLOCK,
        block
    });
    getStore().dispatch({
        type: ACTIONS.SET_EXERCISE_DELETE,
        block,
        setExercise: exercise
    });
    getStore().dispatch({
        type: ACTIONS.UI_DELETE_DIALOG,
        isOpen: false
    });
    ToastState.showSuccessMessage('Exercise Deleted');
    const newBlock = await WorkoutService.deleteSetExercise(
        block.id,
        exercise.id
    );
    if (newBlock.workoutSetExercises.length <= 1) {
        newBlock.exerciseKeys = _.pluck(newBlock.exercises, 'id');
        newBlock.key = newBlock.id;
        updateBlock(getStore().dispatch, newBlock, '', '');
    }
    getStore().dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: false
    });
};

export const deleteSession = (_day, session) => {
    CalendarState.deleteSession(getStore().dispatch, _day, session);
};

export const setPageTitle = (dispatch, pageTitle) => {
    dispatch({
        type: ACTIONS.UI_PAGE_TITLE,
        pageTitle
    });
};

export const toggleDeleteSessionDialog = (
    dispatch,
    _day,
    session,
    isOpen,
    title
) =>
    CalendarState.toggleDeleteSessionDialog(
        dispatch,
        _day,
        session,
        isOpen,
        title
    );

export const createBlock = async (dispatch, session, blockType) => {
    disableEditing(dispatch);
    const key = Utils.getKey();
    const newBlock = {
        workout_id: session.workout_id,
        order: session.setKeys.length + 1,
        type: blockType,
        instruction: '',
        is_redzone: null,
        redzone_type: 0,
        exercises: [],
        exerciseKeys: [],
        key
    };
    newBlock.title = Utils.getBlockTypeText({ type: blockType });
    ToastState.showSuccessMessage('Block Added');

    dispatch({
        type: ACTIONS.BLOCK_ADD,
        block: newBlock,
        session
    });

    if (newBlock.plain_text !== 1) {
        setTimeout(() => {
            enableEditing(dispatch, newBlock);
        }, 0);
    } else {
        setTimeout(() => {
            focusBlock(dispatch, newBlock);
        }, 0);
    }

    await saveNewWorkoutSet(dispatch, newBlock);

    return newBlock;
};

export const link = BlockLinker.link;
export const unlink = BlockLinker.unlink;

export const moveBlock = (dispatch, session, block, direction) => {
    const index = session.setKeys.indexOf(block.key);
    if (index === 0 && direction === -1) {
        return; // nothing to do
    }

    if (index + 1 === session.setKeys.length && direction === 1) {
        return; // nothing to do
    }

    const setKeys = [...session.setKeys];
    const keySwapItem = setKeys[index];
    setKeys[index] = setKeys[index + direction];
    setKeys[index + direction] = keySwapItem;

    // todo: defer this till we 100% real ids
    const workoutSets = [...session.sets];
    const swapItem = workoutSets[index];
    workoutSets[index] = workoutSets[index + direction];
    workoutSets[index + direction] = swapItem;

    const updatedSession = {
        ...session,
        sets: workoutSets, // sets can't be trusted here
        setKeys
    };

    dispatch({
        type: ACTIONS.UI_BLOCK_EDITING,
        blockKey: false
    });

    dispatch({
        type: ACTIONS.SESSION_UPDATE,
        session: updatedSession
    });

    saveWorkoutSetOrder(updatedSession);
};

export const toggleSchemeModal = (exercise, abr, isOpen) => {
    getStore().dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: false
    });
    getStore().dispatch({
        type: ACTIONS.UI_SCHEME_DIALOG,
        exercise,
        abr,
        isOpen
    });
};

export const toggleCircuitDialog = (block, isOpen, edit) => {
    getStore().dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: false
    });
    getStore().dispatch({
        type: ACTIONS.UI_CIRCUIT_DIALOG,
        block,
        isOpen,
        edit
    });
};

export const toggleComboEditDialog = async (exercise, isOpen) => {
    const combo = exercise;
    const dispatch = getStore().dispatch;
    // if (isOpen) {
    //     combo = await FavoriteService.get(exercise.id);
    // }
    dispatch({
        type: ACTIONS.UI_COMBO_EDIT_DIALOG,
        combo,
        isOpen
    });
};

export const showMoreVideos = (dispatch, value) => {
    dispatch({
        type: ACTIONS.UI_MORE_VIDEOS,
        showMoreVideos: value
    });
};

export const toggleDeleteBlockDialog = (block, session, isOpen, title) => {
    getStore().dispatch({
        type: ACTIONS.UI_DELETE_DIALOG,
        block,
        session,
        bind: {
            title,
            type: 'Block'
        },
        isOpen
    });
};

export const toggleDeleteExerciseDialog = (
    dispatch,
    exercise,
    block,
    isOpen,
    title
) => {
    dispatch({
        type: ACTIONS.UI_DELETE_DIALOG,
        exercise,
        block,
        bind: {
            title,
            type: 'Exercise'
        },
        isOpen
    });
};

function trackSessionPublishedEvent(session) {
    const team = getState().calendar.infoTree.team;
    const calendarType = ProgramTypes.convertToString(team.type);
    SegmentService.track(SEGMENT_EVENTS.SessionPublished.key, {
        userId: getState().app.loggedInUser.id,
        email: getState().app.loggedInUser.email,
        calendarType: calendarType,
        programWorkoutId: session.id,
        dateSessionPrescribed: session.date,
        blockCount: session.sets.length
    });
}

export const togglePublished = (dispatch, session, published) => {
    const updatedSession = {
        ...session.data,
        published
    };

    if (published === 1) {
        ProgramService.publishProgramWorkouts([updatedSession.id]).then(() => {
            trackSessionPublishedEvent(updatedSession);
            ToastState.showSuccessMessage('Session Published ');
        });
    } else {
        ProgramService.unPublishProgramWorkout(updatedSession.id).then(() => {
            ToastState.showSuccessMessage('Session Unpublished ');
        });
    }

    dispatch({
        type: ACTIONS.SESSION_UPDATE,
        session: updatedSession
    });
};

export const moveExercise = (dispatch, block, setExercise, direction) => {
    const index = block.exerciseKeys.indexOf(setExercise.id);
    if (index === 0 && direction === -1) {
        return; // nothing to do
    }

    if (index + 1 === block.exerciseKeys.length && direction === 1) {
        return; // nothing to do
    }

    const setExerciseKeys = [...block.exerciseKeys];
    const swapKeyItem = setExerciseKeys[index];
    setExerciseKeys[index] = setExerciseKeys[index + direction];
    setExerciseKeys[index + direction] = swapKeyItem;

    const setExercises = [...block.exercises];
    const swapItem = setExercises[index];
    setExercises[index] = setExercises[index + direction];
    setExercises[index + direction] = swapItem;

    const updatedBlock = {
        ...block,
        exercises: setExercises,
        exerciseKeys: setExerciseKeys
    };

    ProgramService.saveWorkoutSetExerciseOrder(setExercises).then(() => {
        ToastState.showInfoMessage('Exercises Reordered');
    });

    dispatch({
        type: ACTIONS.BLOCK_UPDATE,
        block: updatedBlock
    });
};

const saveWorkoutSetExerciseOrder = _.debounce(setExercises => {
    ProgramService.saveWorkoutSetExerciseOrder(setExercises);
    ToastState.showInfoMessage('Exercises Reordered');
}, 350);

export const createSetExercise = async (block, exercise) => {
    const key = Utils.getKey();
    const default3Sets =
        Utils.isLift(exercise) ||
        repParamTypes.includes(exercise.param_1_type) ||
        repParamTypes.includes(exercise.param_2_type);

    const newSetExercise = {
        exercise_id: exercise.id,
        workout_set_id: block.id,
        set_id: block.id,
        title: exercise.title,
        instruction: '',
        order: block.exerciseKeys.length + 1,
        param_1_type: exercise.param_1_type || workoutParamTypes.NONE,
        param_2_type: exercise.param_2_type || workoutParamTypes.NONE,
        param_1_data_1: '',
        param_1_data_2: '',
        param_1_data_3: '',
        param_1_data_4: '',
        param_1_data_5: '',
        param_1_data_6: '',
        param_1_data_7: '',
        param_1_data_8: '',
        param_1_data_9: '',
        param_1_data_10: '',
        param_2_data_1: '',
        param_2_data_2: '',
        param_2_data_3: '',
        param_2_data_4: '',
        param_2_data_5: '',
        param_2_data_6: '',
        param_2_data_7: '',
        param_2_data_8: '',
        param_2_data_9: '',
        param_2_data_10: '',
        workout_set_exercise_template_id: null,
        no_sets: 0,
        param_count: default3Sets ? 3 : 1,
        set_num: default3Sets ? 3 : 1,
        key,
        setKey: block.key,
        video_url: exercise.video_url,
        thumbnail_url: exercise.thumbnail_url
    };

    newSetExercise.abr = Utils.calculateAbr(newSetExercise);

    if (exercise.tags) {
        newSetExercise.tags = exercise.tags;
    }
    if (exercise.eType) {
        newSetExercise.eType = exercise.eType;
        newSetExercise.use_count = exercise.use_count;
        newSetExercise.isNew = exercise.isNew;
    }

    if (block.exerciseTags) {
        newSetExercise.tags = block.exerciseTags;
    }

    return await saveNewWorkoutSetExercise(newSetExercise, block);
};

export const setBlockPlainText = async (
    dispatch,
    block,
    plainTextExercise = {}
) => {
    disableEditing(dispatch, block);
    let newBlock = null;
    if (plainTextExercise.id) {
        block.exercises = [];
        block.workoutSetExercises = [];
        let updatedBlock = await WorkoutService.replaceWorkoutSetWithCombo(
            block.id,
            plainTextExercise.id
        );
        // if (block.order > 1) {
        const isSuperset =
            plainTextExercise.eType === 'wc' &&
            parseInt(plainTextExercise.plain_text, 10) === 0;
        const type =
            !plainTextExercise.type || plainTextExercise.type === 0
                ? isSuperset
                    ? Utils.BlockTypes.STRENGTH_POWER
                    : Utils.BlockTypes.CONDITIONING
                : plainTextExercise.type;
        const title = isSuperset
            ? plainTextExercise.title
            : Utils.getBlockTypeText({ type });
        updatedBlock = await ProgramService.saveWorkoutSet({
            ...updatedBlock,
            title,
            type
        });
        // }
        ToastState.showSuccessMessage('Block Updated');
        const exercises = [];
        const exerciseKeys = [];
        for (let i = 0; i < updatedBlock.exercises.length; i++) {
            const exercise = updatedBlock.exercises[i];
            exercise.order = i;
            exercise.key = exercise.id;
            exercises.push(exercise);
            exerciseKeys.push(exercise.id);
        }

        updatedBlock.exercises = [];
        updatedBlock.exerciseKeys = [];
        updatedBlock.key = block.key;

        for (let i = 0; i < exercises.length; i++) {
            dispatch({
                type: ACTIONS.BLOCK_ADD_EXERCISE,
                block: updatedBlock,
                exercise: exercises[i],
                isSuperset: plainTextExercise.eType === 'wc'
            });
        }
        updatedBlock.exercises = exercises;
        updatedBlock.exerciseKeys = exerciseKeys;
        dispatch({
            type: ACTIONS.BLOCK_UPDATE,
            block: updatedBlock,
            newExercise: updatedBlock.key
        });
    } else {
        newBlock = {
            ...Utils.emptyCircuitTemplate,
            id: block.id,
            key: block.key,
            order: block.order,
            title: plainTextExercise.label,
            plain_text: plainTextExercise.plain_text,
            type: plainTextExercise.type ? plainTextExercise.type : block.type
        };
        let updatedBlock = await ProgramService.saveWorkoutSet(newBlock);
        updatedBlock = {
            ...updatedBlock,
            key: block.key,
            exerciseKeys: newBlock.exerciseKeys,
            exercises: newBlock.exercises
        };
        ToastState.showSuccessMessage('Block Saved');
        dispatch({
            type: ACTIONS.BLOCK_UPDATE,
            block: updatedBlock,
            newExercise: updatedBlock.key
        });
    }
};

export const swapExercise = (block, setExercise, newExercise) => {
    setExercise = {
        ...setExercise,
        exercise_id: newExercise.id,
        no_sets: newExercise.no_sets,
        title: newExercise.title,
        tags: newExercise.tags,
        video_url: newExercise.video_url,
        video_thumbnail: null
    };

    if (!setExercise.param_count) {
        setExercise.param_1_type = newExercise.param_1_type;
        setExercise.param_2_type = newExercise.param_2_type;
    }

    getStore().dispatch({
        type: ACTIONS.BLOCK_UPDATE_EXERCISE,
        setExercise: { ...setExercise }
    });

    getStore().dispatch({
        type: ACTIONS.BLOCK_SWAP_EXERCISE,
        exercise: { ...setExercise }
    });

    getStore().dispatch({
        type: ACTIONS.UI_BLOCK_SAVED,
        blockSaved: block.id,
        save: false
    });

    saveWorkoutSetExercises([setExercise]);
};

export const updateTags = (dispatch, _setExercise, tags) => {
    const selectedTags = tags || [];
    const updatedSetExercise = {
        ..._setExercise,
        tags: selectedTags
    };
    dispatch({
        type: ACTIONS.BLOCK_UPDATE_EXERCISE,
        setExercise: updatedSetExercise
    });
    saveWorkoutSetExercises([updatedSetExercise]);
};

export const updateSchemeTags = (dispatch, _setExercise, tags) => {
    const tagType = 4;
    const filteredTags = _.filter(
        _setExercise.tags,
        tag => parseInt(tag.type, 10) !== tagType
    );
    const newTags = _.union(tags, filteredTags);
    const updatedSetExercise = {
        ..._setExercise,
        tags: newTags
    };
    dispatch({
        type: ACTIONS.BLOCK_UPDATE_EXERCISE,
        setExercise: updatedSetExercise
    });
    saveWorkoutSetExercises([updatedSetExercise]);
};

// for UI usage only, not persisted until you select an exercise
export const updateExerciseTags = (dispatch, block, tags, limit) => {
    if (limit) {
        ToastState.showWarningMessage(
            'Sorry, can only add 3 categories to this Exercise.'
        );
        return;
    }
    const updatedBlock = {
        ...block,
        exerciseTags: tags
    };
    dispatch({
        type: ACTIONS.BLOCK_UPDATE,
        block: updatedBlock
    });
};

export const updateBlockTags = (dispatch, block, tags, limit) => {
    if (limit) {
        ToastState.showWarningMessage(
            'Sorry, can only add 3 categories to this Exercise.'
        );
        return;
    }
    const updatedBlock = {
        ...block,
        tags
    };
    dispatch({
        type: ACTIONS.SET_CURRENT_BLOCK,
        block: updatedBlock
    });
    dispatch({
        type: ACTIONS.BLOCK_UPDATE,
        block: updatedBlock
    });

    ProgramService.saveWorkoutSet(updatedBlock);
};

export const cancelPreviewRemoval = () => {
    CalendarState.cancelPreview();
};

export const changeBlockType = (dispatch, block, newType) => {
    const isCustomTitle = Utils.isCustomBlockTitle(block.title);
    const title = isCustomTitle
        ? block.title
        : Utils.getBlockTypeText({ type: newType });
    const updatedBlock = {
        ...block,
        title,
        type: newType,
        typeClass: `wstype-${newType}`
    };
    ProgramService.saveWorkoutSet(updatedBlock);

    dispatch({
        type: ACTIONS.BLOCK_UPDATE,
        block: updatedBlock
    });
    dispatch({
        type: ACTIONS.UI_BLOCK_SAVED,
        blockSaved: block.id,
        save: false
    });
};

export const changeBlockProp = async (dispatch, block, prop, val) => {
    const updatedBlock = {
        ...block,
        [prop]: val
    };

    dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: true
    });

    await ProgramService.saveWorkoutSet(updatedBlock);

    ToastState.showSuccessMessage('Block Saved');

    dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: false
    });
    dispatch({
        type: ACTIONS.BLOCK_UPDATE,
        block: updatedBlock
    });
    dispatch({
        type: ACTIONS.UI_BLOCK_SAVED,
        blockSaved: block.id,
        save: false
    });
};

export const toggleTest = (dispatch, block, test) => {
    const updatedBlock = {
        ...block,
        resets_working_max: test ? 1 : 0
    };
    if (test) {
        ToastState.showSuccessMessage(
            'Working maxes will be tested and reset.'
        );
    }

    ProgramService.saveWorkoutSet(updatedBlock);
    dispatch({
        type: ACTIONS.BLOCK_UPDATE,
        block: updatedBlock
    });
    dispatch({
        type: ACTIONS.UI_BLOCK_SAVED,
        blockSaved: block.id,
        save: false
    });
};

export const enableEditing = (dispatch, block) => {
    dispatch({
        type: ACTIONS.UI_BLOCK_EDITING,
        blockKey: block.key
    });
};

export const enableCustomCircuitSave = (dispatch, block, enableCircuitSave) => {
    dispatch({
        type: ACTIONS.UI_ENABLE_CIRCUIT_SAVE,
        block,
        enableCircuitSave
    });
};

export const disableEditing = dispatch => {
    dispatch({
        type: ACTIONS.UI_BLOCK_EDITING,
        blockKey: false
    });
};

export const focusBlock = (dispatch, block) => {
    dispatch({
        type: ACTIONS.UI_BLOCK_FOCUS,
        blockKey: block.key
    });
};

export const changeBlockExerciseType = (dispatch, block, newType) => {
    const updatedBlock = {
        ...block,
        plain_text: newType === 3 ? 0 : 1
    };

    if (newType === 1) {
        updatedBlock.redzone_type = 0;
    }

    ProgramService.saveWorkoutSet(updatedBlock);

    dispatch({
        type: ACTIONS.BLOCK_UPDATE,
        block: updatedBlock
    });
    dispatch({
        type: ACTIONS.UI_BLOCK_SAVED,
        blockSaved: block.id,
        save: false
    });
};

export const setCurrentDay = (dispatch, currentDay) =>
    dispatch({
        type: ACTIONS.SET_CURRENT_DAY,
        currentDay
    });

export const toggleConfirmDialog = (
    dispatch,
    open,
    confirmTitle,
    confirmCb
) => {
    dispatch({
        type: ACTIONS.UI_CONFIRM_DIALOG,
        confirmDialog: open,
        confirmTitle,
        confirmCb
    });
};

export const toggleSupersetSaveDialog = (block, open) => {
    getStore().dispatch({
        type: ACTIONS.UI_SUPERSET_SAVE_DIALOG,
        supersetDialog: open,
        block
    });
};

export const toggleLeaderboard = (dispatch, block, enabled) => {
    const updatedBlock = {
        ...block,
        is_redzone: enabled ? 1 : 0
    };

    ProgramService.saveWorkoutSet(updatedBlock);

    dispatch({
        type: ACTIONS.BLOCK_UPDATE,
        block: updatedBlock
    });
};

export const changeToEmptyBlock = block => {
    const updatedBlock = {
        ...block
    };
    const toasted = false;

    updatedBlock.title = 'Circuit';
    updatedBlock.workout_combo_id = null;

    ProgramService.saveWorkoutSet(updatedBlock).then(() => {
        if (!toasted) {
            ToastState.showSuccessMessage('Block Saved');
        }
    });

    getStore().dispatch({
        type: ACTIONS.BLOCK_UPDATE,
        block: updatedBlock
    });
    getStore().dispatch({
        type: ACTIONS.UI_BLOCK_SAVED,
        blockSaved: block.id,
        save: false
    });
};

export const savingState = (dispatch, block, uiSaving) => {
    if (uiSaving) {
        ToastState.showLoadingMessage('Saving ...');
    }
    getStore().dispatch({
        type: ACTIONS.UI_SAVING,
        uiSaving,
        block
    });
};

export const waitingState = () => {
    ToastState.showLoadingMessage('Saving previous changes...');
};

export const updateBlock = (dispatch, block, key, value) => {
    const updatedBlock = {
        ...block
    };

    if (updatedBlock.hasOwnProperty(key)) {
        updatedBlock[key] = value;
    }

    let toastMessage = false;

    if (key === 'redzone_type') {
        updatedBlock.is_redzone = value > 0 ? 1 : 0;
        if (value > 0) toastMessage = 'Leaderboard created for this block.';
    }

    dispatch({
        type: ACTIONS.BLOCK_UPDATE,
        block: updatedBlock
    });

    ProgramService.saveWorkoutSet(updatedBlock).then(response => {
        const realTitle = Utils.getBlockTitle(response.order - 1);
        toastMessage = toastMessage || `Block ${realTitle} Saved`;
        ToastState.showSuccessMessage(toastMessage);
        savingState(block, false);
    });
};

export const updateRepScheme = (dispatch, block, setExercise, scheme) => {
    setExercise = {
        ...setExercise,
        workout_set_exercise_template_id: scheme.id
    };

    if (scheme) {
        setExercise = {
            ...setExercise,
            customScheme: false,
            ...scheme,
            id: setExercise.id,
            title: setExercise.title,
            tags: setExercise.tags
        };
    }

    dispatch({
        type: ACTIONS.BLOCK_UPDATE_EXERCISE,
        block,
        setExercise
    });

    dispatch({
        type: ACTIONS.SCHEME_UPDATE,
        exercise: setExercise,
        custom: scheme.custom === 1
    });

    dispatch({
        type: ACTIONS.UI_BLOCK_SAVED,
        blockSaved: block.id,
        save: false
    });

    saveWorkoutSetExercises([setExercise]);
};

export const updateSetNum = (dispatch, block, setExercise, newParamCount) => {
    const increasing = setExercise.param_count < newParamCount;

    if (increasing && setExercise.param_count !== 0) {
        const lastRowKey1 = `param_1_data_${setExercise.param_count}`;
        const lastRowKey2 = `param_2_data_${setExercise.param_count}`;
        const lastRowDataValue1 = setExercise[lastRowKey1];
        const lastRowDataValue2 = setExercise[lastRowKey2];
        for (let i = setExercise.param_count; i <= newParamCount; i++) {
            const key1 = `param_1_data_${i}`;
            const key2 = `param_2_data_${i}`;
            setExercise[key1] = lastRowDataValue1;
            setExercise[key2] = lastRowDataValue2;
        }
    }

    setExercise = {
        ...setExercise,
        param_count: newParamCount,
        no_sets: newParamCount === 0 ? 1 : 0
    };

    for (let i = 10; i > newParamCount; i--) {
        const key1 = `param_1_data_${i}`;
        const key2 = `param_2_data_${i}`;
        setExercise[key1] = '';
        setExercise[key2] = '';
    }

    const schemeId = Utils.getSchemeId(
        setExercise,
        getState().library.schemeRef
    );
    setExercise.workout_set_exercise_template_id = schemeId;

    saveWorkoutSetExercises([setExercise]);

    dispatch({
        type: ACTIONS.BLOCK_UPDATE_EXERCISE,
        setExercise
    });
};

export const updateExerciseValueState = (
    dispatch,
    setExercise,
    keyName,
    value
) => {
    setExercise = {
        ...setExercise
    };
    setExercise[keyName] = value;

    const abr = Utils.calculateAbrToSave(setExercise);
    const schemeId = Utils.getSchemeId(
        setExercise,
        getState().library.schemeRef
    );
    setExercise.abr = abr;
    setExercise.workout_set_exercise_template_id = schemeId;

    dispatch({
        type: ACTIONS.BLOCK_UPDATE_EXERCISE,
        setExercise
    });
    return setExercise;
};

export const saveUpdatedExerciseValues = async setExercise => {
    const response = await WorkoutService.saveWorkoutSetExercises([
        setExercise
    ]);
    setExercise.id = parseInt(response[0].id, 10);
    getStore().dispatch({
        type: ACTIONS.BLOCK_UPDATE_EXERCISE_IDS,
        setExercise
    });
    getStore().dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: false
    });
};

export const saveUpdatedExerciseValuesDebounced = _.debounce(
    saveUpdatedExerciseValues,
    200
);

export const updateExerciseValue = (dispatch, setExercise, keyName, value) => {
    setExercise = {
        ...setExercise
    };
    setExercise[keyName] = value;

    const abr = Utils.calculateAbrToSave(setExercise);
    const schemeId = Utils.getSchemeId(
        setExercise,
        getState().library.schemeRef
    );
    setExercise.abr = abr;
    setExercise.workout_set_exercise_template_id = schemeId;

    saveWorkoutSetExercises([setExercise]);

    dispatch({
        type: ACTIONS.BLOCK_UPDATE_EXERCISE,
        setExercise
    });
};

export const updateExerciseParamType = (
    dispatch,
    setExercise,
    paramNumber,
    value
) => {
    const paramKey = `param_${paramNumber}_type`;
    const paramDataKey = `param_${paramNumber}_data_`;

    setExercise = {
        ...setExercise
    };
    setExercise[paramKey] = value;

    if (value === 0) {
        let i = 10;
        while (i--) {
            setExercise[`${paramDataKey}${i + 1}`] = '';
        }
    }

    const schemeId = Utils.getSchemeId(
        setExercise,
        getState().library.schemeRef
    );
    setExercise.workout_set_exercise_template_id = schemeId;
    saveWorkoutSetExercises([setExercise]);

    dispatch({
        type: ACTIONS.BLOCK_UPDATE_EXERCISE,
        setExercise
    });
};

export const toggleSchemeSave = (dispatch, setExercise, value) => {
    setExercise = {
        ...setExercise,
        schemeSaveEnabled: value,
        schemeName: ''
    };

    dispatch({
        type: ACTIONS.BLOCK_UPDATE_EXERCISE,
        setExercise
    });
};

export const saveCustomScheme = async (setExercise, abr, tags) => {
    getStore().dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: true
    });
    const scheme = {
        title: setExercise.abr,
        type: 1,
        param_count: setExercise.param_count,
        param_1_type: setExercise.param_1_type,
        param_2_type: setExercise.param_2_type || workoutParamTypes.NONE,
        param_1_data_1: setExercise.param_1_data_1,
        param_1_data_2: setExercise.param_1_data_2,
        param_1_data_3: setExercise.param_1_data_3,
        param_1_data_4: setExercise.param_1_data_4,
        param_1_data_5: setExercise.param_1_data_5,
        param_1_data_6: setExercise.param_1_data_6,
        param_1_data_7: setExercise.param_1_data_7,
        param_1_data_8: setExercise.param_1_data_8,
        param_1_data_9: setExercise.param_1_data_9,
        param_1_data_10: setExercise.param_1_data_10,
        param_2_data_1: setExercise.param_2_data_1,
        param_2_data_2: setExercise.param_2_data_2,
        param_2_data_3: setExercise.param_2_data_3,
        param_2_data_4: setExercise.param_2_data_4,
        param_2_data_5: setExercise.param_2_data_5,
        param_2_data_6: setExercise.param_2_data_6,
        param_2_data_7: setExercise.param_2_data_7,
        param_2_data_8: setExercise.param_2_data_8,
        param_2_data_9: setExercise.param_2_data_9,
        param_2_data_10: setExercise.param_2_data_10,
        custom: 0,
        no_sets: 0
    };

    const savedScheme = await WorkoutElementService.saveSetExerciseTemplate(
        scheme,
        tags
    );
    ToastState.showSuccessMessage('Prescription Saved');
    setExercise = {
        ...setExercise,
        schemeSaveEnabled: false,
        workout_set_exercise_template_id: savedScheme.id,
        custom_scheme: 0
    };

    getStore().dispatch({
        type: ACTIONS.SCHEME_ADD,
        scheme: { ...savedScheme, abr: savedScheme.title }
    });

    getStore().dispatch({
        type: ACTIONS.BLOCK_UPDATE_EXERCISE,
        setExercise
    });

    getStore().dispatch({
        type: ACTIONS.UI_SCHEME_DIALOG,
        exercise: setExercise,
        isOpen: false
    });

    saveWorkoutSetExercises([setExercise]);
};

export const saveBlockAsSuperset = async (block, title) => {
    getStore().dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: true,
        block
    });
    // Save original block with new title
    const updatedBlock = {
        ...block,
        title
    };

    // Update the original block with the new title
    let savedBlock = await ProgramService.saveWorkoutSet(updatedBlock);

    savedBlock = {
        ...savedBlock,
        ...updatedBlock,
        eType: 'wc'
    };

    // Create a superset in the library with the new title
    const superset = await WorkoutElementService.saveAsNewCombo(savedBlock.id);

    // superset.icon = block.icon;

    getStore().dispatch({
        type: ACTIONS.EXERCISES_BENCHMARK_REF_ADD,
        exercise: superset,
        block
    });
    getStore().dispatch({
        type: ACTIONS.UI_SUPERSET_SAVE_DIALOG,
        supersetDialog: false,
        block
    });
    getStore().dispatch({
        type: ACTIONS.BLOCK_UPDATE,
        block: updatedBlock
    });
    getStore().dispatch({
        type: ACTIONS.UI_BLOCK_SAVED,
        blockSaved: block.id,
        save: false
    });

    getStore().dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: false,
        block: updatedBlock
    });
};

export const saveAndAddExercise = async (block, _setExercise, newExercise) => {
    getStore().dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: true
    });
    const response = await ExerciseService.apiCreate(newExercise);
    const exerciseWithDefaults = {
        ...response,
        eType: 'e',
        isNew: true,
        param_1_type:
            response.param_1_type != null
                ? response.param_1_type
                : newExercise.param_1_type,
        param_2_type:
            response.param_2_type != null
                ? response.param_2_type
                : newExercise.param_2_type
    };

    getStore().dispatch({
        type: ACTIONS.EXERCISES_REF_ADD,
        exercise: exerciseWithDefaults
    });

    if (_setExercise.id) {
        swapExercise(block, _setExercise, exerciseWithDefaults);
    } else {
        if (!block.id) {
            const blockId = await waitForBlockId(block.key);
            block = { ...block, id: blockId };
        }
        await createSetExercise(block, exerciseWithDefaults);
    }

    getStore().dispatch({
        type: ACTIONS.UI_NEW_DIALOG,
        isOpen: false
    });
    getStore().dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: false
    });
};

export const toggleExerciseDialog = (
    block,
    _setExercise,
    isOpen,
    createNew = false
) => {
    const clearTags = true;

    disableEditing(getStore().dispatch, block);
    setTimeout(() => {
        getStore().dispatch({
            type: ACTIONS.UI_SAVING,
            isSaving: false
        });
        getStore().dispatch({
            type: ACTIONS.UI_NEW_DIALOG,
            setExercise: _setExercise,
            block,
            isOpen,
            clearTags,
            createNew
        });
    }, 100);
};

export const createTag = async tagTitle => {
    const tag = { title: tagTitle };
    const response = await TagService.createTag(tag);

    getStore().dispatch({
        type: ACTIONS.UI_CREATE_TAG,
        tag: response.data
    });

    return response;
};

export const createTrainingEffectTag = async tagTitle => {
    const tag = { title: tagTitle };
    const response = await TagService.createTrainingEffectTag(tag);

    getStore().dispatch({
        type: ACTIONS.UI_CREATE_TAG,
        tag: response.data
    });
    return response.data;
};

export const createCircuit = async (circuitData, tagsIds, block) => {
    getStore().dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: true
    });
    let isTest = null;
    let rawFavorite = await FavoriteService.create();
    const exercisesIds = _.map(
        circuitData.exercises,
        ex => (ex.exercise_id ? ex.exercise_id : ex.id)
    );
    if ((rawFavorite && rawFavorite.isTest) || (block && block.is_test)) {
        isTest = block.is_test || rawFavorite.is_test;
    } else {
        isTest = 0;
    }

    rawFavorite = {
        ...rawFavorite,
        exercisesIds,
        instruction: circuitData.instruction,
        is_redzone: circuitData.is_redzone || 0,
        is_test: isTest,
        redzone_instruction:
            block.redzone_instruction || rawFavorite.redzone_instruction,
        redzone_type: circuitData.redzone_type || 0,
        test_instruction:
            block.test_instruction || rawFavorite.test_instruction,
        test_type: block.test_type || rawFavorite.test_type,
        tags: tagsIds,
        title: circuitData.title,
        tracker_id: block.tracker_id || rawFavorite.tracker_id,
        type: block.type || rawFavorite.type
    };
    const favorite = await FavoriteService.saveWorkoutCombo(rawFavorite);
    favorite.eType = 'wc';
    favorite.isNew = true;

    block.exercises = circuitData.exercises;

    getStore().dispatch({
        type: ACTIONS.EXERCISES_BENCHMARK_REF_ADD,
        exercise: favorite
    });

    getStore().dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: false
    });

    getStore().dispatch({
        type: ACTIONS.UI_CIRCUIT_DIALOG,
        block: false,
        isOpen: false
    });

    getStore().dispatch({
        type: ACTIONS.UI_ENABLE_CIRCUIT_SAVE,
        block,
        enableCircuitSave: false
    });

    setBlockPlainText(getStore().dispatch, block, favorite);
    ToastState.showSuccessMessage('Circuit Saved');
};

export const saveCombo = async (dispatch, combo, session, block) => {
    dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: true
    });
    combo.exercisesIds = _.pluck(combo.exercises, 'id');
    combo.tags = _.pluck(combo.tags, 'id');
    const savedCombo = await FavoriteService.saveWorkoutCombo(combo);
    savedCombo.key = savedCombo.id;
    savedCombo.icon = combo.icon;
    savedCombo.can_edit = combo.can_edit;
    savedCombo.eType = combo.eType;
    dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: false
    });
    dispatch({
        type: ACTIONS.EXERCISE_UPDATE,
        exercise: savedCombo,
        block,
        session
    });
    dispatch({
        type: ACTIONS.UI_COMBO_EDIT_DIALOG,
        savedCombo,
        isOpen: false,
        isEdit: false
    });
};

export const scrollTo = (dispatch, type, key) => {
    dispatch({
        type,
        key
    });
};

export const showPreview = async (item, position, type = null) => {
    await CalendarState.showPreview(item, position, type);
};

export const clearScrollTo = (dispatch, type) => {
    dispatch({
        type,
        key: null
    });
};

export const setSelectedDate = function(date) {
    getStore().dispatch({
        type: ACTIONS.SET_SELECTED_DATE,
        date
    });
};

export const getSelectedDate = function() {
    return getStore().getState().calendar.infoTree.designerInfo.dayId;
};

export const waitForBlockId = blockKey =>
    new Promise(resolve => {
        const existing = getState().calendar.blockTree[blockKey];
        if (existing && existing.id) return resolve(existing.id);
        const unsubscribe = getStore().subscribe(() => {
            const stored = getState().calendar.blockTree[blockKey];
            if (stored && stored.id) {
                unsubscribe();
                resolve(stored.id);
            }
        });
    });

export const saveNewWorkoutSet = async (dispatch, workoutSet) => {
    const response = await ProgramService.saveProgramWorkoutSets([workoutSet]);
    dispatch({
        type: ACTIONS.BLOCK_UPDATE_ID,
        block: {
            ...workoutSet,
            id: response[0].id
        }
    });
    return response[0];
};

// private helper methods
function saveWorkoutSetOrder(session) {
    const workoutSets = [];
    const blockTree = getState().calendar.blockTree;
    for (let i = 0; i < session.setKeys.length; i++) {
        const block = blockTree[session.setKeys[i]];
        if (!block.id) {
            setTimeout(() => {
                saveWorkoutSetOrder(session);
            }, 2000);
            return;
        }
        workoutSets.push(block.id);
    }
    ProgramService.saveWorkoutSetOrder(workoutSets).then(() => {
        ToastState.showInfoMessage('Blocks Reordered');
    });
}

export async function saveWorkoutSetExercises(setExercises, resolution) {
    for (let i = 0; i < setExercises.length; i++) {
        const setExercise = setExercises[i];
        const response = await WorkoutService.saveWorkoutSetExercises([
            setExercise
        ]);
        setExercise.id = parseInt(response[0].id, 10);
        getStore().dispatch({
            type: ACTIONS.BLOCK_UPDATE_EXERCISE_IDS,
            setExercise
        });
        getStore().dispatch({
            type: ACTIONS.UI_SAVING,
            isSaving: false
        });
    }
    resolution && resolution();
}

async function saveNewWorkoutSetExercise(setExercise, block) {
    const response = await WorkoutService.saveWorkoutSetExercises([
        setExercise
    ]);
    setExercise.id = parseInt(response[0].id, 10);
    setExercise.key = parseInt(response[0].id, 10);
    setExercise.video_thumbnail = response[0].video_thumbnail
        ? response[0].video_thumbnail
        : setExercise.video_thumbnail;
    setExercise.video_url = response[0].video_url
        ? response[0].video_url
        : setExercise.video_url;
    getStore().dispatch({
        type: ACTIONS.BLOCK_ADD_EXERCISE,
        block,
        exercise: setExercise
    });
    getStore().dispatch({
        type: ACTIONS.BLOCK_UPDATE_EXERCISE_IDS,
        setExercise
    });

    getStore().dispatch({
        type: ACTIONS.UI_SAVING,
        isSaving: false
    });
}

export const setInfo = (programType, params) => {
    const config = getDesignerConfig(programType, params);

    getStore().dispatch({
        type: ACTIONS.DESIGNER_SET_INFO,
        info: config
    });
};

const getDesignerConfig = (programType, params) => {
    if (ProgramTypes.isProgram(programType)) {
        let dateData = {
            weekIndex: params.weekIndex || 0,
            dayIndex: params.dayIndex || 0
        };
        const weekIndex = dateData.weekIndex;
        const dayIndex = dateData.dayIndex;
        return {
            dayId: weekIndex + '_' + dayIndex,
            isProgram: true
        };
    }

    const now = DateUtils.now();
    params = params || {
        year: now.format('YYYY'),
        month: now.format('MM'),
        day: now.format('DD')
    };

    const year = params.year;
    const month = params.month;
    const day = params.day;

    return {
        dayId: year + '_' + month + '_' + day,
        year,
        month,
        day,
        isProgram: false
    };
};

export const unsubscribeCalendar = calendar => {
    const sessionTree = getState().calendar.sessionTree;
    const dayTree = getState().calendar.dayTree;
    _.each(sessionTree, session => {
        const isSessionOnCalendar =
            session.data.program_id === calendar.group_program;
        if (isSessionOnCalendar) {
            const dayKey = Utils.getDayKey(session.data);
            const day = dayTree[dayKey];

            if (day) {
                getStore().dispatch({
                    type: ACTIONS.SESSION_DELETE,
                    day,
                    session: session.data
                });
            }
        }
    });
};
